import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback } from 'react';
import { PATH_URL } from '#constants';
import { getPartialLoginState } from '#utils';

type QueryParamsHandling = '' | 'merge' | 'preserve';

type GoToSignedInAppOptions = {
  queryParamsHandling?: QueryParamsHandling;
  shouldRefreshPage?: boolean;
  // twoFactorSettings?: TwoFactorSettings;
  isLinkedIn?: boolean;
  skipRedirect?: boolean;
  emailVerifyRequired?: boolean;
};

type GoTo2FACodeOptions = {
  redirectUrl: string;
};

export function useAuthNavigation() {
  const router = useRouter();
  const searchParams = useSearchParams();

  /**
   * Navigate within the /purchase zone using Next.js router.
   * The router automatically prepends the basePath (/purchase).
   */
  const navigateWithinZone = useCallback(
    (path: string) => {
      router.push(path);
    },
    [router],
  );

  /**
   * Navigate to a route outside the /purchase zone (e.g. /two-factor).
   * Uses window.location.assign to bypass Next.js basePath prefixing.
   */
  const navigateOutsideZone = useCallback((url: URL) => {
    if (url.origin !== window.location.origin) {
      throw new Error('Invalid redirect URL: must be same origin');
    }
    window.location.assign(url.pathname + url.search);
  }, []);

  const goTo2FACode = useCallback(
    ({ redirectUrl }: GoTo2FACodeOptions) => {
      const twoFactorSettings = getPartialLoginState().twoFactorSettings;

      const redirectQueryParams =
        getPartialLoginState().redirectQueryParams ?? {};

      const twoFactorUrl = twoFactorSettings?.two_factor_type
        ? PATH_URL.TWO_FACTOR
        : PATH_URL.TWO_FACTOR_SETUP;

      const url = new URL(`/${twoFactorUrl}`, window.location.origin);

      for (const [key, value] of searchParams.entries()) {
        url.searchParams.set(key, value);
      }

      for (const [key, value] of Object.entries(redirectQueryParams)) {
        url.searchParams.set(key, value);
      }

      if (twoFactorSettings?.two_factor_type) {
        url.searchParams.set('twofa', twoFactorSettings.two_factor_type);
      }

      // Ensure the redirect URL includes the /purchase base path, since 2FA
      // lives outside the zone and needs the full path to navigate back.
      // also strip any existing /purchase prefix first to avoid double-prefixing.
      const strippedUrl = redirectUrl.replace(/^\/?purchase\//, '');
      const fullRedirectUrl = `/purchase/${strippedUrl}`;
      url.searchParams.set('r', fullRedirectUrl);

      navigateOutsideZone(url);
    },
    [navigateOutsideZone, searchParams],
  );

  // todo(hao): hanndle all other sign in cases (2fa, sso, shared speech)
  const goToSignedInApp = useCallback(
    ({ skipRedirect = false, emailVerifyRequired }: GoToSignedInAppOptions) => {
      const redirectUrl: string =
        getPartialLoginState().redirectUrl ||
        searchParams.get('r') ||
        PATH_URL.HOME;
      const redirectQueryParams =
        getPartialLoginState().redirectQueryParams ?? {};

      const twoFactorSettings = getPartialLoginState().twoFactorSettings;

      const pathsNotRequireEmailVerify: string[] = [
        PATH_URL.WELCOME,
        PATH_URL.EMAIL_VERIFY,
        PATH_URL.PURCHASE_PRO_PLAN,
        PATH_URL.PURCHASE_TEAM_PLAN,
        // paths for the zone
        PATH_URL.PRO,
        PATH_URL.TEAM,
      ].map((p) => p.replace(/^\//, ''));

      if (
        twoFactorSettings?.two_factor_required ||
        twoFactorSettings?.two_factor_type
      ) {
        goTo2FACode({ redirectUrl });
      } else if (
        emailVerifyRequired &&
        !pathsNotRequireEmailVerify.includes(redirectUrl)
      ) {
        navigateWithinZone(`/${PATH_URL.EMAIL_VERIFY}`);
      } else if (skipRedirect) {
        return;
      } else {
        const normalizedPath = redirectUrl.startsWith('/')
          ? redirectUrl
          : `/${redirectUrl}`;
        const url = new URL(normalizedPath, window.location.origin);

        // Copy all search params except 'r' from searchParams to url
        searchParams.forEach((value, key) => {
          if (key !== 'r') {
            url.searchParams.set(key, value);
          }
        });

        for (const [key, value] of Object.entries(redirectQueryParams)) {
          url.searchParams.set(key, value);
        }

        navigateWithinZone(url.pathname + url.search);
      }
    },
    [goTo2FACode, navigateWithinZone, searchParams],
  );

  return {
    goToSignedInApp,
    goTo2FACode,
  };
}
